import { VexRoutes } from '@vex/interfaces/vex-route.interface';
import { LayoutComponent } from './layouts/layout/layout.component';
import { AuthGuard, PublicGuard } from '@shared/angular/cognito';
import { PermissionCode } from '@shared/common/enums';

export const appRoutes: VexRoutes = [
    {
        path: 'auth',
        canActivate: [PublicGuard],
        loadChildren: () => import('./pages/auth/auth.routes'),
    },
    {
        path: 'invitation',
        loadComponent: () =>
            import('./pages/invitation/invitation.component').then((m) => m.InvitationComponent),
    },
    {
        path: 'app/:path',
        loadComponent: () =>
            import('./pages/app-redirect/app-redirect.component').then(
                (m) => m.AppRedirectComponent,
            ),
    },
    {
        path: '',
        component: LayoutComponent,
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        children: [
            {
                path: 'dashboard',
                redirectTo: '/',
                pathMatch: 'full',
            },
            {
                path: 'users/admin',
                title: 'Administrators',
                data: {
                    permissions: [
                        PermissionCode.VIEW_USER,
                        PermissionCode.CREATE_USER,
                        PermissionCode.UPDATE_USER,
                    ],
                },
                loadComponent: () =>
                    import('./pages/users/users.component').then((m) => m.UsersComponent),
            },
            {
                path: 'users/customer',
                title: 'Customers',
                data: {
                    permissions: [
                        PermissionCode.VIEW_CUSTOMER,
                        PermissionCode.CREATE_CUSTOMER,
                        PermissionCode.UPDATE_CUSTOMER,
                    ],
                },
                loadComponent: () =>
                    import('./pages/customers/customers.component').then(
                        (m) => m.CustomersComponent,
                    ),
            },
            {
                path: 'merchants',
                redirectTo: 'merchants/all',
                pathMatch: 'full',
            },
            {
                path: 'merchants/detail/:merchantId',
                title: 'Merchant Detail',
                data: {
                    permissions: [PermissionCode.VIEW_MERCHANT],
                },
                loadComponent: () =>
                    import(
                        './pages/merchants/detail/merchant-detail.component'
                    ).then((m) => m.MerchantDetailComponent),
            },
            {
                path: 'merchants/:status',
                title: 'Merchants',
                data: {
                    permissions: [
                        PermissionCode.VIEW_MERCHANT,
                        PermissionCode.CREATE_MERCHANT,
                        PermissionCode.UPDATE_MERCHANT,
                        PermissionCode.DISABLE_ENABLE_MERCHANT,
                        PermissionCode.MANAGE_MERCHANT_DOCUMENT,
                        PermissionCode.MERCHANT_ACCEPTING_PAYMENTS,
                    ],
                },
                loadComponent: () =>
                    import('./pages/merchants/merchants.component').then(
                        (m) => m.MerchantsComponent,
                    ),
            },

            {
                path: 'users/sub-agents/:userType',
                title: 'Sub Accounts',
                data: {
                    permissions: [
                        PermissionCode.VIEW_SUB_AGENT,
                        PermissionCode.CREATE_SUB_AGENT,
                        PermissionCode.VIEW_WHITELABEL,
                        PermissionCode.CREATE_WHITELABEL,
                    ],
                },
                loadComponent: () =>
                    import('./pages/sub-agents/sub-agents.component').then(
                        (m) => m.SubAgentsComponent,
                    ),
            },
            {
                path: 'user-groups/:userType',
                title: 'User Groups',
                data: {
                    permissions: [
                        PermissionCode.VIEW_USER_ROLE,
                        PermissionCode.CREATE_USER_ROLE,
                        PermissionCode.UPDATE_USER_ROLE,
                        PermissionCode.DELETE_USER_ROLE,
                    ],
                },
                loadComponent: () =>
                    import('./pages/user-groups/user-groups.component').then(
                        (m) => m.UserGroupsComponent,
                    ),
            },
            {
                path: 'settings',
                title: 'Settings',
                loadChildren: () => import('./pages/settings/settings.routes'),
            },
            {
                path: 'visa-management',
                title: 'Visa Management',
                data: {
                    permissions: [
                        PermissionCode.VIEW_VISA,
                        PermissionCode.CREATE_VISA,
                        PermissionCode.VIEW_VISA_ACKNOWLEDGEMENT,
                        PermissionCode.VIEW_VISA_FILE_REQUIREMENT,
                    ],
                },
                loadChildren: () => import('./pages/visa-management/visa-management.routes'),
            },
            {
                path: 'manual-booking-management',
                title: 'Manual Booking Management',
                data: {
                    permissions: [
                        PermissionCode.VIEW_TRAVEL_INSURANCE,
                        PermissionCode.CREATE_TRAVEL_INSURANCE,
                        PermissionCode.UPDATE_TRAVEL_INSURANCE,
                        PermissionCode.DELETE_TRAVEL_INSURANCE,
                        PermissionCode.VIEW_TRAVEL_INSURANCE_FILE_REQUIREMENT,
                        PermissionCode.VIEW_TRAVEL_INSURANCE_ACKNOWLEDGEMENT,
                        PermissionCode.VIEW_IMMIGRATION_SERVICE,
                        PermissionCode.CREATE_IMMIGRATION_SERVICE,
                        PermissionCode.UPDATE_IMMIGRATION_SERVICE,
                        PermissionCode.DELETE_IMMIGRATION_SERVICE,
                        PermissionCode.VIEW_IMMIGRATION_SERVICE_FILE_REQUIREMENT,
                        PermissionCode.VIEW_IMMIGRATION_SERVICE_ACKNOWLEDGEMENT,
                    ],
                },
                loadChildren: () =>
                    import('./pages/manual-booking-management/manual-booking-management.routes'),
            },
            {
                path: 'driving-permit-management',
                title: 'Driving Permit Management',
                data: {
                    permissions: [
                        PermissionCode.VIEW_DRIVING_PERMIT,
                        PermissionCode.CREATE_DRIVING_PERMIT,
                        PermissionCode.VIEW_DRIVING_PERMIT_ACKNOWLEDGEMENT,
                        PermissionCode.VIEW_DRIVING_PERMIT_FILE_REQUIREMENT,
                    ],
                },
                loadChildren: () =>
                    import('./pages/driving-permit-management/driving-permit-management.routes'),
            },
            {
                path: 'passport-management',
                title: 'Passport Management',
                data: {
                    permissions: [
                        PermissionCode.VIEW_PASSPORT_APPLICATION_TYPE,
                        PermissionCode.VIEW_PASSPORT_REQUIREMENT,
                        PermissionCode.VIEW_PASSPORT_ACKNOWLEDGEMENT,
                        PermissionCode.VIEW_PASSPORT_DFA_SITE,
                    ],
                },
                loadChildren: () =>
                    import('./pages/passport-management/passport-management.routes').then(
                        (m) => m.passportManagementRoutes,
                    ),
            },
            {
                path: 'kyc/:status',
                title: 'Customer KYC Applications',
                data: {
                    permissions: [
                        PermissionCode.VIEW_KYC_APPLICATION,
                        PermissionCode.REVIEW_KYC_APPLICATION,
                    ],
                },
                loadComponent: () =>
                    import('./pages/kyc/kyc.component').then((m) => m.KycComponent),
            },
            {
                path: 'nfc-card-requests/:status',
                title: 'Physical Card Requests',
                data: {
                    permissions: [
                        PermissionCode.VIEW_NFC_CARD_REQUEST,
                        PermissionCode.REVIEW_NFC_CARD_REQUEST,
                    ],
                },
                loadComponent: () =>
                    import(
                        './pages/nfc-card-requests/nfc-card-requests.component'
                    ).then((m) => m.NfcCardRequestsComponent),
            },
            {
                path: 'risk-review',
                title: 'Risk Review',
                data: {
                    permissions: [PermissionCode.VIEW_RISK_REVIEW],
                },
                loadComponent: () =>
                    import('./pages/risk-review/risk-review.component').then(
                        (m) => m.RiskReviewComponent,
                    ),
            },
            {
                path: 'fund-request/:status',
                title: 'Fund Request',
                data: {
                    permissions: [
                        PermissionCode.VIEW_FUND_REQUEST,
                        PermissionCode.CREATE_FUND_REQUEST,
                        PermissionCode.REVIEW_FUND_REQUEST,
                    ],
                },
                loadComponent: () =>
                    import('./pages/fund-request/fund-request.component').then(
                        (m) => m.FundRequestComponent,
                    ),
            },
            {
                path: 'fund-manual',
                title: 'Fund Manual',
                data: {
                    permissions: [
                        PermissionCode.VIEW_MANUAL_FUND,
                        PermissionCode.DEBIT_MANUAL_FUND,
                        PermissionCode.CREDIT_MANUAL_FUND,
                    ],
                },
                loadComponent: () =>
                    import('./pages/fund-manual/fund-manual.component').then(
                        (m) => m.FundManualComponent,
                    ),
            },
            {
                path: 'accounting',
                title: 'Accounting',
                data: { permissions: [PermissionCode.VIEW_ACCOUNTING_REPORT] },
                loadChildren: () =>
                    import('./pages/accounting/accounting.routes').then(
                        (m) => m.ACCOUNTING_ROUTES,
                    ),
            },
            {
                path: 'chat',
                title: 'Chat Assistance',
                data: {
                    permissions: [PermissionCode.VIEW_CHAT_SUPPORT],
                },
                loadChildren: () => import('./pages/chat/chat.routes'),
            },
            {
                path: 'visa-application',
                title: 'Visa Applications',
                data: {
                    permissions: [
                        PermissionCode.VIEW_VISA_APPLICATION,
                        PermissionCode.CREATE_VISA_APPLICATION,
                        PermissionCode.CHANGE_STATUS_VISA_APPLICATION,
                    ],
                },
                loadChildren: () => import('./pages/visa-application/visa-application.routes'),
            },
            {
                path: 'driving-permit-application',
                title: 'Driving Permit Applications',
                data: {
                    permissions: [
                        PermissionCode.VIEW_DRIVING_PERMIT_APPLICATION,
                        PermissionCode.CREATE_DRIVING_PERMIT_APPLICATION,
                        PermissionCode.CHANGE_STATUS_DRIVING_PERMIT_APPLICATION,
                    ],
                },
                loadChildren: () =>
                    import('./pages/driving-permit-application/driving-permit-application.routes'),
            },
            {
                path: 'manual-booking',
                title: 'Manual Booking Applications',
                data: {
                    permissions: [
                        PermissionCode.VIEW_TRAVEL_INSURANCE_APPLICATION,
                        PermissionCode.CREATE_TRAVEL_INSURANCE_APPLICATION,
                        PermissionCode.CHANGE_STATUS_TRAVEL_INSURANCE_APPLICATION,
                        PermissionCode.VIEW_IMMIGRATION_SERVICE_APPLICATION,
                        PermissionCode.CREATE_IMMIGRATION_SERVICE_APPLICATION,
                        PermissionCode.CHANGE_STATUS_IMMIGRATION_SERVICE_APPLICATION,
                    ],
                },
                loadChildren: () =>
                    import('./pages/manual-booking-application/manual-booking-application.routes'),
            },
            {
                path: 'user-api',
                title: 'User APIs',
                data: {
                    permissions: [
                        PermissionCode.VIEW_USER_API_KEY,
                        PermissionCode.CREATE_USER_API_KEY,
                        PermissionCode.DELETE_USER_API_KEY,
                        PermissionCode.UPDATE_USER_API_KEY,
                    ],
                },
                loadComponent: () =>
                    import('./pages/user-api/user-api.component').then((m) => m.UserApiComponent),
            },
            {
                path: '',
                title: 'Dashboard',
                loadComponent: () =>
                    import('./pages/dashboard/dashboard.component').then(
                        (m) => m.DashboardComponent,
                    ),
            },
            {
                path: 'ewallet-transactions/:accountId',
                title: 'E-Wallet Transactions',
                data: {
                    permissions: [PermissionCode.VIEW_MANUAL_FUND],
                },
                loadComponent: () =>
                    import('./pages/ewallet-transactions/ewallet-transactions.component').then(
                        (m) => m.EWalletTransactionsComponent,
                    ),
            },
            {
                path: 'flight-bookings',
                title: 'Flight Bookings',
                data: {
                    permissions: [PermissionCode.VIEW_FLIGHT_BOOKING],
                },
                loadComponent: () =>
                    import('./pages/flight-bookings/flight-bookings.component').then(
                        (m) => m.FlightBookingsComponent,
                    ),
            },
            {
                path: 'hotel-bookings',
                title: 'Hotel Bookings',
                data: {
                    permissions: [PermissionCode.VIEW_HOTEL_BOOKING],
                },
                loadComponent: () =>
                    import('./pages/hotel-bookings/hotel-bookings.component').then(
                        (m) => m.HotelBookingsComponent,
                    ),
            },
            {
                path: 'passport-application/list',
                title: 'Passport Applications',
                data: {
                    permissions: [
                        PermissionCode.VIEW_PASSPORT_APPLICATION,
                        PermissionCode.CREATE_PASSPORT_APPLICATION,
                    ],
                },
                loadComponent: () =>
                    import('./pages/passport/passport.component').then((m) => m.PassportComponent),
            },
            {
                path: 'travel-packages',
                title: 'Travel Packages',
                data: {
                    permissions: [
                        PermissionCode.VIEW_TOUR_PACKAGE,
                        PermissionCode.CREATE_TOUR_PACKAGE,
                    ],
                },
                loadChildren: () => import('./pages/travel-packages/travel-packages.routes'),
            },
            {
                path: 'travel-bookings',
                title: 'Travel Bookings',
                data: {
                    permissions: [PermissionCode.VIEW_TOUR_PACKAGE_BOOKING],
                },
                loadChildren: () => import('./pages/travel-bookings/travel-bookings.routes'),
            },
            {
                path: 'nfc-management',
                title: 'NFC Card Management',
                data: {
                    permissions: [PermissionCode.VIEW_NFC_CARD],
                },
                loadChildren: () =>
                    import('./pages/nfc-management/nfc-management.module').then(
                        (m) => m.NFCManagementModule,
                    ),
            },
            {
                path: 'cms',
                title: 'Content Management',
                data: {
                    permissions: [
                        PermissionCode.VIEW_CMS_POST,
                        PermissionCode.VIEW_CMS_CATEGORY,
                    ],
                },
                loadChildren: () => import('./pages/cms/cms.routes'),
            },
            {
                path: 'faq-management',
                title: 'FAQ Management',
                data: {
                    permissions: [PermissionCode.VIEW_FAQ],
                },
                loadChildren: () => import('./pages/faq-management/faq.routes'),
            },
            {
                path: '',
                children: [
                    {
                        path: 'pricing',
                        loadComponent: () =>
                            import('./pages/pages/pricing/pricing.component').then(
                                (m) => m.PricingComponent,
                            ),
                    },
                    {
                        path: 'error-404',
                        loadComponent: () =>
                            import('./pages/errors/error-404.component').then(
                                (m) => m.Error404Component,
                            ),
                    },
                    {
                        path: 'error-500',
                        loadComponent: () =>
                            import('./pages/errors/error-500.component').then(
                                (m) => m.Error500Component,
                            ),
                    },
                    {
                        path: 'forbidden',
                        loadComponent: () =>
                            import('./pages/errors/error-403.component').then(
                                (m) => m.Error403Component,
                            ),
                    },
                ],
            },
            {
                path: '**',
                title: 'Page Not Found',
                loadComponent: () =>
                    import('./pages/errors/error-404.component').then((m) => m.Error404Component),
            },
        ],
    },
];
